import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "../components/Button";
import Card from "../components/Card";
import { LockIcon } from "../components/Icons";
import { authAPI } from "../services/api";

const OTPVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resending, setResending] = useState(false);
  const inputRefs = useRef([]);

  const email = location.state?.email || "";
  const type = location.state?.type || "verification"; // 'verification' or 'reset'
  const userNumber = location.state?.userNumber || "";

  useEffect(() => {
    if (!email) {
      navigate("/login");
    }
  }, [email, navigate]);

  const handleChange = (index, value) => {
    if (value.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError("");

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const otpCode = otp.join("");
    if (otpCode.length !== 6) {
      setError("Please enter complete OTP");
      return;
    }

    setLoading(true);

    try {
      if (type === "reset") {
        // Verify reset OTP
        const response = await authAPI.verifyResetOTP(email, otpCode);
        if (response.success) {
          // Navigate to reset password with token
          navigate("/reset-password", {
            state: {
              resetToken: response.resetToken,
            },
          });
        }
      } else {
        // Verify email OTP
        const response = await authAPI.verifyOTP(email, otpCode);
        if (response.success) {
          setSuccess("Email verified successfully! Redirecting...");
          setTimeout(() => {
            navigate("/dashboard");
          }, 1500);
        }
      }
    } catch (err) {
      setError(err.error || "Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setSuccess("");
    setResending(true);

    try {
      const response = await authAPI.resendOTP(email);
      if (response.success) {
        setSuccess("OTP sent successfully!");
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();

        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      setError(err.error || "Failed to resend OTP");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-main)] flex items-center justify-center px-4 sm:px-6 md:px-8">
      <Card className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 card-gradient rounded-full flex items-center justify-center">
            <LockIcon className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-2">Verify OTP</h1>
          <p className="text-[var(--text-primary)]">Enter the 6-digit code sent to</p>
          <p className="text-[var(--text-primary)] font-semibold mt-1">{email}</p>
          {userNumber ? (
            <p className="text-sm text-[var(--text-primary)] opacity-80 mt-2">
              Save your{" "}
              <span className="font-semibold text-primary">User ID</span>:{" "}
              <span className="font-mono font-bold tracking-wide">{userNumber}</span>
            </p>
          ) : null}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg">
            <p className="text-red-500 text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-500/10 border border-green-500/50 rounded-lg">
            <p className="text-green-500 text-sm">{success}</p>
          </div>
        )}

        <form onSubmit={handleVerify} className="space-y-6">
          <div className="flex justify-center gap-2 sm:gap-3">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-14 sm:w-14 sm:h-16 bg-card-gradient text-[var(--text-primary)] text-center text-2xl font-bold rounded-xl border border-primary/20 focus:border-primary focus:outline-none transition-all duration-200"
                disabled={loading}
              />
            ))}
          </div>

          <Button type="submit" fullWidth disabled={loading}>
            {loading ? "Verifying..." : "Verify Code"}
          </Button>

          <div className="text-center">
            <p className="text-[var(--text-primary)] text-sm mb-2">Didn't receive the code?</p>
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="text-[var(--text-primary)] hover:underline font-semibold text-sm disabled:opacity-50"
            >
              {resending ? "Sending..." : "Resend Code"}
            </button>
          </div>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate("/login")}
            className="text-muted hover:text-[var(--text-primary)] text-sm transition-colors"
          >
            ← Back to Login
          </button>
        </div>
      </Card>
    </div>
  );
};

export default OTPVerification;
